/**
 * Eligibility gate.
 *
 * A question opts into the gate by putting the `#is_sick` marker in its
 * description. On such a question every choice except `not_sick` is treated as
 * a disqualifying answer: the survey is held back until the user is shown the
 * "Formula AI is not a fit right now" sheet. The sheet is informational — once
 * it is acknowledged the onboarding continues, and the disqualifying answers
 * are reported to the host when the survey completes.
 *
 * The marker is an authoring flag, not user-facing copy, so it is stripped out
 * of the description before the question is rendered.
 */

export const ELIGIBILITY_GATE_MARKER = "#is_sick";
export const ELIGIBLE_CHOICE_VALUE = "not_sick";

const MARKER_PATTERN = /#is_sick/gi;

function stripMarker(text) {
  if (typeof text !== "string") return text;
  return text.replace(MARKER_PATTERN, "").replace(/\s+/g, " ").trim();
}

function hasMarker(text) {
  return typeof text === "string" && text.toLowerCase().includes(ELIGIBILITY_GATE_MARKER);
}

/**
 * Descriptions may be a plain string or a `{ default, ru, ... }` locale map,
 * so both shapes have to be inspected.
 */
function readDescriptionJson(question) {
  const locDescription = question?.locDescription;
  if (locDescription && typeof locDescription.getJson === "function") {
    const json = locDescription.getJson();
    if (json !== undefined && json !== null) return json;
  }
  return question?.description;
}

function descriptionHasMarker(descriptionJson) {
  if (hasMarker(descriptionJson)) return true;
  if (descriptionJson && typeof descriptionJson === "object") {
    return Object.values(descriptionJson).some((value) => hasMarker(value));
  }
  return false;
}

/**
 * `question.description = ...` only writes the *active* locale, so a marker
 * living in `default` would survive and still be shown as a fallback.
 * `locDescription.setJson()` replaces the whole locale map in one go, and
 * clearing it that way also turns `hasDescription` off, so no empty
 * description card is left behind.
 */
function writeStrippedDescription(question, descriptionJson) {
  const locDescription = question?.locDescription;
  const canSetJson = locDescription && typeof locDescription.setJson === "function";

  if (typeof descriptionJson === "string") {
    const stripped = stripMarker(descriptionJson);
    if (canSetJson) {
      locDescription.setJson(stripped);
    } else {
      question.description = stripped;
    }
    return;
  }

  if (!descriptionJson || typeof descriptionJson !== "object") return;

  const stripped = {};
  let hasRemainingText = false;

  Object.keys(descriptionJson).forEach((localeKey) => {
    const value = stripMarker(descriptionJson[localeKey]);
    stripped[localeKey] = value;
    if (typeof value === "string" && value.length > 0) {
      hasRemainingText = true;
    }
  });

  if (!canSetJson) {
    question.description = "";
    return;
  }

  locDescription.setJson(hasRemainingText ? stripped : "");
}

/**
 * Finds every gate question, removes the marker from its description and
 * returns the set of their names.
 */
export function collectEligibilityGateQuestions(survey) {
  const gateQuestionNames = new Set();
  if (!survey || typeof survey.getAllQuestions !== "function") return gateQuestionNames;

  survey.getAllQuestions().forEach((question) => {
    if (!question) return;

    const descriptionJson = readDescriptionJson(question);
    if (!descriptionHasMarker(descriptionJson)) return;

    gateQuestionNames.add(question.name);
    writeStrippedDescription(question, descriptionJson);
  });

  return gateQuestionNames;
}

/**
 * True when the question carries an answer that disqualifies the user, i.e.
 * anything other than the single "none of these" choice.
 */
export function hasDisqualifyingAnswer(question) {
  const value = question?.value;
  if (value === undefined || value === null) return false;

  const selectedValues = Array.isArray(value) ? value : [value];
  return selectedValues.some((selectedValue) => {
    if (selectedValue === undefined || selectedValue === null) return false;
    const normalized = String(selectedValue).trim();
    if (!normalized) return false;
    return normalized.toLowerCase() !== ELIGIBLE_CHOICE_VALUE;
  });
}

/**
 * Names of the gate questions the user is currently disqualified on. Used at
 * completion time to tell the host that no automatic plan should be built,
 * even though the user was allowed to finish the onboarding.
 */
export function getDisqualifyingQuestionNames(survey, gateQuestionNames) {
  if (!survey || typeof survey.getAllQuestions !== "function") return [];
  if (!gateQuestionNames || typeof gateQuestionNames.has !== "function") return [];

  return survey
    .getAllQuestions()
    .filter(
      (question) =>
        question && gateQuestionNames.has(question.name) && hasDisqualifyingAnswer(question)
    )
    .map((question) => question.name);
}
